/**
 * Story #6490 (Bug #4838) 2.6 / 3.6 / 8.1 — the step-up retry for axios-based
 * key actions (the organisation page's Revoke and Offboard), the same shape as
 * CustodyTierPanel's tier change: call optimistically; on a 403 carrying
 * requires_webauthn: true run the security-key ceremony for `operation` and
 * retry exactly ONCE with the token. A second 403 is thrown to the caller —
 * the token is single-use, so retrying again could never succeed.
 */
import { stepUp as realStepUp, isWebAuthnAvailable, NO_AUTHENTICATOR } from '../../services/webauthnClient'

export const STEP_UP_NO_AUTHENTICATOR_TEXT =
  'This action needs a security key on your custody tier. Register one under Manage devices, then try again.'
export const STEP_UP_UNSUPPORTED_TEXT =
  'This action needs a security key, but this browser doesn’t support WebAuthn.'

export function requiresWebAuthn403(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { requires_webauthn?: boolean } } }
  return e?.response?.status === 403 && e?.response?.data?.requires_webauthn === true
}

export interface StepUpDeps {
  stepUp?: (operation: string) => Promise<string>
  webAuthnAvailable?: () => boolean
}

export async function withStepUp<T>(
  call: (webauthnToken?: string) => Promise<T>, operation: string, deps: StepUpDeps = {},
): Promise<T> {
  const stepUp = deps.stepUp ?? realStepUp
  const webAuthnAvailable = deps.webAuthnAvailable ?? isWebAuthnAvailable
  try {
    return await call()
  } catch (err) {
    if (!requiresWebAuthn403(err)) throw err
    if (!webAuthnAvailable()) throw new Error(STEP_UP_UNSUPPORTED_TEXT)
    let token: string
    try {
      token = await stepUp(operation)
    } catch (se) {
      if ((se as Error)?.message === NO_AUTHENTICATOR) throw new Error(STEP_UP_NO_AUTHENTICATOR_TEXT)
      throw se
    }
    return call(token)
  }
}
